import React, { FC, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';
import { ParamsProps } from 'src/app/common/components/ParamsProvider';
import PruFilter, { PruFilterItemType } from 'src/app/common/components/PruTable/PruFilter';
import PruTableCustom from 'src/app/common/components/PruTable/PruTableCustom';
import { useDataProvider } from 'src/app/common/utils';
import { getDayStart, getDayEnd, getDisplayDate } from 'src/app/common/utils/time-utils';
import { fetchActivityList, fetchActivityDownload } from 'src/app/modules/Report/network/livestreamReportCrud';
import { ActivityListParams, ActivityPaginateList } from '../types/activity-type';

type ActivityListingPageProps = {} & ParamsProps;

const initialState: ActivityListParams = {
  requestedDateFrom: getDayStart(new Date()),
  requestedDateTo: getDayEnd(new Date()),
  agentCode: '',
  livestreamName: '',
  page: 1,
  limit: 5,
};

const paramsInitiator = (initialParams?: Record<string, string>): ActivityListParams => {
  return initialParams
    ? {
        requestedDateFrom: initialParams.requestedDateFrom
          ? getDayStart(new Date(initialParams.requestedDateFrom))
          : getDayStart(new Date()),
        requestedDateTo: initialParams.requestedDateTo
          ? getDayEnd(new Date(initialParams.requestedDateTo))
          : getDayEnd(new Date()),
        agentCode: '',
        livestreamName: '',
        page: 1,
        limit: 5,
      }
    : initialState;
};

const ActivityListingPage: FC<ActivityListingPageProps> = ({ initialParams }) => {
  const intl = useIntl();
  const Translation = (id: string) => intl.formatMessage({ id });
  const dispatch = useDispatch();

  const [filterState, setFilterState] = useState<ActivityListParams>(paramsInitiator(initialParams));
  const [reportList, setReportList] = useState<ActivityPaginateList>();

  const { isLoading, refreshData } = useDataProvider<ActivityPaginateList>(
    async () => {
      try {
        return await fetchActivityList(filterState, dispatch);
      } catch (err) {}
    },
    setReportList,
    false,
  );

  const fetchCsvDownload = () => {
    try {
      fetchActivityDownload(filterState, dispatch);
    } catch (err) {}
  };

  return (
    <>
      <PruFilter
        title={Translation('livestream.report.common.filter')}
        itemDef={[
          {
            type: PruFilterItemType.DATE_RANGE,
            fieldFrom: 'requestedDateFrom',
            fieldTo: 'requestedDateTo',
            initialDateFrom: filterState.requestedDateFrom,
            initialDateTo: filterState.requestedDateTo,
            displayName: Translation('report.common.requestDate'),
            defaultDateFrom: initialState.requestedDateFrom,
            defaultDateTo: initialState.requestedDateTo,
            periodTime: { months: 3 },
            required: true
          },
          {
            type: PruFilterItemType.FREE_TEXT,
            field: 'agentCode',
            initialValue: filterState.agentCode,
            displayName: Translation('livestream.report.common.agentCode'),
          },
          {
            type: PruFilterItemType.FREE_TEXT,
            field: 'livestreamName',
            initialValue: filterState.livestreamName,
            displayName: Translation('livestream.report.common.livestreamName'),
          },
        ]}
        onChangeFilter={(data) =>
          setFilterState({
            ...filterState,
            requestedDateFrom: data.requestedDateFrom,
            requestedDateTo: data.requestedDateTo,
            agentCode: data.agentCode,
            livestreamName: data.livestreamName,
          })
        }
        fetchData={refreshData}
      />
      <PruTableCustom
        disableBulkSelect
        dataSource={reportList?.items}
        isLoading={isLoading}
        onRefresh={refreshData}
        isCombineHeader={true}
        title={Translation('livestream.activity.title')}
        headerBtnDef={[
          {
            color: 'primary',
            title: Translation('report.common.export'),
            onClick: () => fetchCsvDownload(),
          },
        ]}
        operationDef={[]}
        columnDef={[
          {
            isId: true,
            hidden: true,
            keyIndex: '_id',
            displayName: '',
            renderData: () => '',
          },
          {
            keyIndex: 'agentCode',
            displayName: Translation('livestream.report.common.agentCode'),
            rowSpan: 2,
            renderData: (row: any) => row.agentCode,
          },
          {
            keyIndex: 'agentName',
            displayName: Translation('livestream.report.common.agentName'),
            rowSpan: 2,
            renderData: (row: any) => row.agentName,
          },
          {
            keyIndex: 'officeCode',
            displayName: Translation('livestream.report.common.officeCode'),
            rowSpan: 2,
            renderData: (row: any) => row.officeCode,
          },
          {
            keyIndex: 'livestreamName',
            displayName: Translation('livestream.report.common.livestreamName'),
            rowSpan: 2,
            renderData: (row: any) => row.livestreamName,
          },
          {
            keyIndex: 'livestreamStartAt',
            displayName: Translation('livestream.report.common.livestreamStartAt'),
            rowSpan: 2,
            renderData: (row: any) => getDisplayDate(row.livestreamStartAt, 'YYYY-MM-DD'),
          },
          {
            keyIndex: 'streaming',
            displayName: Translation('livestream.report.common.streaming'),
            align: 'center',
            colspan: 3,
            renderData: (row: any) => '',
          },
          {
            keyIndex: 'firstJoin',
            displayName: Translation('livestream.activity.formLabel.firstJoin'),
            isCombine: true,
            renderData: (row: any) => (row.firstJoin ? getDisplayDate(row.firstJoin, 'HH:mm:ss') : ''),
          },
          {
            keyIndex: 'lastExit',
            displayName: Translation('livestream.activity.formLabel.lastExit'),
            isCombine: true,
            renderData: (row: any) => (row.lastExit ? getDisplayDate(row.lastExit, 'HH:mm:ss') : ''),
          },
          {
            keyIndex: 'views',
            displayName: Translation('livestream.activity.formLabel.views'),
            isCombine: true,
            renderData: (row: any) => (row.views || 0).toString(),
          },
          {
            keyIndex: 'noOfPlayback',
            displayName: Translation('livestream.activity.formLabel.noOfPlayback'),
            rowSpan: 2,
            renderData: (row: any) => (row.noOfPlayback || 0).toString(),
          },
          {
            keyIndex: 'latestPlaybackDate',
            displayName: Translation('livestream.activity.formLabel.latestPlaybackDate'),
            rowSpan: 2,
            renderData: (row: any) =>
              row.latestPlaybackDate ? getDisplayDate(row.latestPlaybackDate, 'YYYY-MM-DD') : '',
          },
          {
            keyIndex: 'noOfShare',
            displayName: Translation('livestream.activity.formLabel.noOfShare'),
            rowSpan: 2,
            renderData: (row: any) => (row.noOfShare || 0).toString(),
          },
        ]}
        totalPages={reportList?.totalPages}
        totalRecords={reportList?.totalRecords}
        onChangePage={(page, rowsPerPage) => {
          setFilterState({
            ...filterState,
            page,
            limit: rowsPerPage,
          });
          refreshData();
        }}
      />
    </>
  );
};

export default ActivityListingPage;
